/**
 * Auth calls added by #2324 (required password change, Google sign-in).
 *
 * They live here, not in services/api.ts, because api.ts is claimed by
 * Mario's active plan `2026-08-22-staff-editing-documents.md`. They use the
 * same shared client, so the base URL, the locale header and the refresh
 * interceptor all apply. `apiClient` is read inside each function, never at
 * module load, so a test that mocks '@/services/api' without it only fails
 * the call that needs it.
 */
import { apiClient } from '@/services/api';
import type { AuthProviders, LoginResponse } from '@/types/api';

export async function submitRequiredPasswordChange(
  changeToken: string,
  newPassword: string,
  rememberMe: boolean,
): Promise<LoginResponse> {
  const r = await apiClient.post<LoginResponse>('/auth/required-password-change', {
    change_token: changeToken,
    new_password: newPassword,
    rememberMe,
  });
  return r.data;
}

export async function getAuthProviders(): Promise<AuthProviders> {
  const r = await apiClient.get<{ status: string; data?: AuthProviders }>('/auth/providers');
  return r.data.data ?? {};
}

/** Never answers 401 (plan D1), so the refresh interceptor leaves its errors alone. */
export async function signInWithGoogle(credential: string, rememberMe: boolean): Promise<LoginResponse> {
  const r = await apiClient.post<LoginResponse>('/auth/google', { credential, rememberMe });
  return r.data;
}

export async function linkGoogleAccount(credential: string): Promise<{ email: string }> {
  const r = await apiClient.post<{ status: string; data: { google_link: { email: string } } }>(
    '/auth/google/link',
    { credential },
  );
  return r.data.data.google_link;
}

export async function unlinkGoogleAccount(): Promise<void> {
  await apiClient.delete('/auth/google/link');
}
